import React, { useState } from "react";
import { api, errMessage } from "../api";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Newsletter opt-in. Stores locally on the server always; pushes to
 * EmailOctopus when a key is configured. The visitor never sees the difference.
 */
export default function Newsletter({
  heading = "Get the next date.",
  promise = "One email when something is on. No drip campaigns.",
  source = "site",
  testidPrefix = "newsletter",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [msg, setMsg] = useState("");
  const [touched, setTouched] = useState(false);

  const invalid = touched && !EMAIL.test(email.trim());

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!EMAIL.test(email.trim())) {
      setMsg("That email doesn't look quite right.");
      setState("error");
      return;
    }
    setState("sending");
    setMsg("");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        name: name.trim(),
        source,
      });
      setState("done");
      setMsg(
        data.status === "already_subscribed"
          ? "You were already on the list. We'll write when there's something worth coming to."
          : "You're on. We'll write when there's something worth coming to.",
      );
    } catch (err) {
      setState("error");
      setMsg(errMessage(err, "We couldn't add you just now. Try again?"));
    }
  };

  if (state === "done") {
    return (
      <div className="nl" data-testid={`${testidPrefix}-success`}>
        <p className="eyebrow sc">
          <span className="num">&middot;</span>
          <span>On the list</span>
        </p>
        <h3>Thank you.</h3>
        <p className="promise">{msg}</p>
      </div>
    );
  }

  return (
    <div className="nl" data-testid={`${testidPrefix}-block`}>
      <p className="eyebrow sc">
        <span className="num">&middot;</span>
        <span>Notes</span>
      </p>
      <h3>{heading}</h3>
      <p className="promise">{promise}</p>
      <form onSubmit={submit} noValidate>
        <div className="hp" aria-hidden="true">
          <label htmlFor={`${testidPrefix}-hp`}>Leave this empty</label>
          <input id={`${testidPrefix}-hp`} tabIndex={-1} autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor={`${testidPrefix}-email`}>Email</label>
          <input
            id={`${testidPrefix}-email`}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@somewhere.com"
            value={email}
            aria-invalid={invalid ? "true" : "false"}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            data-testid={`${testidPrefix}-email-input`}
          />
          {invalid ? (
            <span className="err" data-testid={`${testidPrefix}-email-error`}>
              <span className="lab">Note</span>
              That email doesn&rsquo;t look quite right.
            </span>
          ) : null}
        </div>
        <div className="field" style={{ flex: "0 1 12rem" }}>
          <label htmlFor={`${testidPrefix}-name`}>
            First name <span className="opt">&mdash; optional</span>
          </label>
          <input
            id={`${testidPrefix}-name`}
            value={name}
            autoComplete="given-name"
            onChange={(e) => setName(e.target.value)}
            data-testid={`${testidPrefix}-name-input`}
          />
        </div>
        <button
          type="submit"
          className="act"
          disabled={state === "sending"}
          data-testid={`${testidPrefix}-submit-button`}
        >
          {state === "sending" ? "Adding\u2026" : "Join the list \u2192"}
        </button>
      </form>
      {state === "error" && msg ? (
        <div className="notice" data-testid={`${testidPrefix}-error`}>
          {msg}
        </div>
      ) : null}
    </div>
  );
}
